import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import './PaymentSuccess.css';


import { API } from '../config';

export default function PaymentSuccess({ user }) {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentInfo, setPaymentInfo] = useState(null);
  const navigate = useNavigate();
  
  // Get session_id from URL
  const rawSessionId = searchParams.get('session_id');
  // Decode and remove any curly braces that might be present
  const sessionId = rawSessionId 
    ? decodeURIComponent(rawSessionId).replace(/[{}]/g, '') 
    : null;
  
  // Debug logging
  console.log('Raw session_id from URL:', rawSessionId);
  console.log('Decoded session_id:', sessionId);
  console.log('Full URL:', window.location.href);

  useEffect(() => {
    if (sessionId) {
      console.log('Starting payment status check for session:', sessionId);
      
      // Check if this is a loan repayment
      const isLoanRepayment = searchParams.get('loan_repayment') === 'true';
      if (isLoanRepayment) {
        confirmLoanRepayment(sessionId);
      } else {
        pollPaymentStatus(sessionId, 0);
      }
    } else {
      console.error('No session_id found in URL parameters');
      setStatus('failed');
      toast.error('No payment session found in URL');
    }
  }, [sessionId]);

  const confirmLoanRepayment = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const pendingData = JSON.parse(localStorage.getItem('pending_loan_repayment') || '{}');
      
      if (!pendingData.loan_id || !pendingData.member_id) {
        // Fall back to regular payment status check
        pollPaymentStatus(sessionId, 0);
        return;
      }
      
      const encodedSessionId = encodeURIComponent(sessionId);
      const response = await axios.post(
        `${API}/members/${pendingData.member_id}/loans/${pendingData.loan_id}/confirm-payment?session_id=${encodedSessionId}`,
        {},
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      localStorage.removeItem('pending_loan_repayment');
      
      if (response.data.message) {
        setStatus('success');
        setPaymentInfo({
          ...response.data,
          payment_type: 'loan_repayment',
          loan_completed: response.data.loan_completed
        });
        toast.success(response.data.loan_completed ? 'Loan fully repaid!' : 'Loan payment successful!');
      } else {
        // Payment not yet confirmed, poll
        setTimeout(() => confirmLoanRepayment(sessionId), 2000);
      }
    } catch (error) {
      console.error('Loan repayment confirmation error:', error);
      if (error.response?.status === 404) {
        // Session might not be ready yet, retry
        setTimeout(() => confirmLoanRepayment(sessionId), 2000);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to confirm loan payment');
        setStatus('failed');
      }
    }
  };

  const pollPaymentStatus = async (sessionId, attempts) => {
    const maxAttempts = 10; // Increased from 5 to 10 for more time
    const pollInterval = 2000;

    // Check if session_id contains placeholder (Stripe didn't replace it)
    if (sessionId.includes('CHECKOUT_SESSION_ID') || sessionId.includes('{') || sessionId.includes('}')) {
      console.error('Session ID contains placeholder text:', sessionId);
      setStatus('failed');
      toast.error('Invalid payment session ID. Please try making a payment again.');
      return;
    }

    if (attempts >= maxAttempts) {
      console.log('Max polling attempts reached');
      setStatus('timeout');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Check if token exists
      if (!token) {
        console.error('No authentication token found');
        toast.error('Session expired. Please log in again.');
        setStatus('failed');
        return;
      }
      
      // Encode session_id for URL
      const encodedSessionId = encodeURIComponent(sessionId);
      console.log(`Checking payment status for session: ${sessionId}`);
      console.log(`Encoded for API call: ${encodedSessionId}`);
      
      const response = await axios.get(`${API}/payments/checkout/status/${encodedSessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Payment status response:', response.data);
      
      // Check for successful payment - handle various success states
      if (response.data.payment_status === 'paid' || response.data.status === 'complete') {
        setStatus('success');
        setPaymentInfo(response.data);
        toast.success('Payment successful!');
      } else if (response.data.status === 'expired' || response.data.status === 'canceled') {
        setStatus('failed');
        toast.error('Payment session expired or was canceled');
      } else if (response.data.status === 'open') {
        // Payment still in progress, continue polling
        console.log(`Payment still processing (attempt ${attempts + 1}/${maxAttempts})`);
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        // Unknown status, continue polling
        console.log(`Unknown status: ${response.data.status}, payment_status: ${response.data.payment_status}`);
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      console.error('Error details:', error.response?.data);
      console.error('Session ID being checked:', sessionId);
      
      // Handle specific error cases
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
        setStatus('failed');
      } else if (error.response?.status === 404) {
        const errorMsg = error.response?.data?.detail || 'Payment session not found';
        console.error('404 Error - Full error message:', errorMsg);
        toast.error(`${errorMsg}. Session ID: ${sessionId.substring(0, 20)}...`);
        setStatus('failed');
      } else if (attempts < maxAttempts - 1) {
        // Retry on other errors (network issues, etc)
        console.log(`Retrying... (attempt ${attempts + 1}/${maxAttempts})`);
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to check payment status');
        setStatus('failed');
      }
    }
  };

  const goToDashboard = () => {
    if (user.role === 'leadership') {
      navigate('/dashboard');
    } else {
      navigate('/group-selection');
    }
  };

  return (
    <div className="payment-success-page" data-testid="payment-success-page">
      <div className="payment-success-container">
        <div className="payment-success-card card">
          {status === 'checking' && (
            <>
              <div className="payment-icon">⏳</div>
              <h1>Processing Payment...</h1>
              <p>Please wait while we confirm your payment.</p>
              <div className="spinner"></div>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="payment-icon success">✅</div>
              <h1>{paymentInfo?.payment_type === 'loan_repayment' 
                ? (paymentInfo?.loan_completed ? 'Loan Fully Repaid!' : 'Loan Payment Successful!')
                : 'Payment Successful!'}</h1>
              <p>{paymentInfo?.payment_type === 'loan_repayment'
                ? (paymentInfo?.loan_completed 
                    ? 'Congratulations! Your loan has been fully repaid.' 
                    : 'Your loan repayment has been recorded.')
                : 'Your payment has been processed successfully.'}</p>
              {paymentInfo && (
                <div className="payment-details">
                  <div className="detail-item">
                    <span>Amount Paid:</span>
                    <span className="amount">${(paymentInfo.amount_paid || paymentInfo.amount || 0).toFixed(2)}</span>
                  </div>
                  {paymentInfo.total_repaid != null && (
                    <div className="detail-item">
                      <span>Total Repaid:</span>
                      <span>${paymentInfo.total_repaid.toFixed(2)}</span>
                    </div>
                  )}
                  {paymentInfo.remaining != null && paymentInfo.remaining > 0 && (
                    <div className="detail-item">
                      <span>Remaining:</span>
                      <span>${paymentInfo.remaining.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span>Status:</span>
                    <span className="status-badge status-approved">Paid</span>
                  </div>
                </div>
              )}
              <button 
                onClick={goToDashboard} 
                className="btn btn-primary"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </button>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="payment-icon failed">❌</div>
              <h1>Payment Failed</h1>
              <p>We couldn't process your payment. Please try again.</p>
              {sessionId && (
                <div style={{ 
                  marginTop: '20px', 
                  padding: '10px', 
                  background: '#f5f5f5', 
                  borderRadius: '4px',
                  fontSize: '12px',
                  wordBreak: 'break-all'
                }}>
                  <strong>Debug Info:</strong><br/>
                  Session ID: {sessionId}
                </div>
              )}
              <button onClick={goToDashboard} className="btn btn-primary">
                Return to Dashboard
              </button>
            </>
          )}

          {status === 'timeout' && (
            <>
              <div className="payment-icon">⚠️</div>
              <h1>Payment Verification Delayed</h1>
              <p>We're still confirming your payment with Stripe. This usually takes a few moments.</p>
              <p className="info-text">Your payment may have been processed successfully. Please check your dashboard or email confirmation.</p>
              <button onClick={goToDashboard} className="btn btn-primary">
                Go to Dashboard
              </button>
              <button 
                onClick={() => {
                  setStatus('checking');
                  pollPaymentStatus(sessionId, 0);
                }} 
                className="btn btn-secondary"
                style={{ marginTop: '10px' }}
              >
                Check Again
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}